import type { ReactNode, SyntheticEvent } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

import type { components } from "../api/schema";
import { docUrl } from "../lib/docLinks";
import { isApiRequestError } from "../lib/errors";
import {
  Button,
  Grid,
  GridItem,
  HelpTip,
  Inline,
  InlineError,
  ModelSelect,
  PageHeader,
  Select,
  Spinner,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "../ui";
import {
  AssertionCaseList,
  AssertionDefaultSelect,
  hasScopeParams,
  OutputSpecFields,
  ResultHandlingSelect,
  ReviewPolicySelect,
  ReviewSampleField,
  ScopeParams,
  ScopeSelect,
} from "./MarkerAuthoringFields";
import type {
  AssertionCaseDraft,
  AuthoringEvaluator,
  DefinitionDraft,
  JudgeModelPolicy,
  LocatorDraft,
  MarkerAutomate,
  MarkerCreate,
  MarkerOutput,
  MarkerRevise,
} from "./markerAuthoringDraft";
import {
  buildAutomateBody,
  buildRevisionBody,
  declaredCategories,
  definitionSeed,
  judgeModelError,
  judgeModelOptionLabel,
  judgeModelOptionsByRef,
  newCase,
  outputSpecApplies,
} from "./markerAuthoringDraft";
import { VariablePatternField } from "./variables/VariablePatternField";

export type { JudgeModelPolicy } from "./markerAuthoringDraft";

type MarkerResponse = components["schemas"]["MarkerResponse"];

type CreateBody = Omit<MarkerCreate, "agent_id">;

export type MarkerAuthoringMode = "create" | "revise" | "automate";

export type MarkerAuthoringSubmission =
  | { body: CreateBody; mode: "create" }
  | { body: MarkerRevise; markerId: string; mode: "revise" }
  | { body: MarkerAutomate; markerId: string; mode: "automate" };

export type MarkerAuthoringSubmit = (
  submission: MarkerAuthoringSubmission,
) => Promise<void>;

export type MarkerAuthoringStatus =
  | { stage: "idle" }
  | { stage: "submitting" }
  | { message: string; stage: "success" }
  | { message: string; stage: "validation_error" }
  | { message: string; stage: "api_error" };

export interface MarkerAuthoringFormProps {
  agentId?: string;
  judgeModelPolicy: JudgeModelPolicy;
  marker?: MarkerResponse;
  mode: MarkerAuthoringMode;
  onCancel?: () => void;
  // §4c Authoring leave-guard signal: true after the first edit, false on
  // reseed and once a submit is handed to the caller (restored on failure) so
  // a post-save navigation is never blocked. Optional and additive — the form
  // behaves identically when omitted.
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmit: MarkerAuthoringSubmit;
  renderHeader?: (actions: ReactNode) => ReactNode;
}

const outputOptions: Array<{ label: string; value: MarkerOutput }> = [
  { label: "Boolean (true / false)", value: "boolean" },
  { label: "Number", value: "number" },
  { label: "Category", value: "category" },
];
// Example copy for the free-text fields. Each example is a complete,
// working input in the shape a strong prompt takes: the value to return,
// the evidence that earns it, and what to ignore.
const descriptionPlaceholder: Record<MarkerOutput, string> = {
  boolean: "Did the agent confirm the caller's shipping address?",
  category: "What was the caller's primary reason for contacting us?",
  number: "How empathetic was the agent, from 1 to 5?",
};

function rubricPlaceholder(draft: DefinitionDraft): string {
  if (draft.output === "number") {
    return (
      "Rate the agent's empathy from 1 to 5. 5: acknowledges the caller's " +
      "feelings and adapts tone throughout. 3: polite but scripted. 1: " +
      "dismissive or interrupts. Cite the utterance that most influenced " +
      "the score."
    );
  }
  if (draft.output === "category") {
    return (
      "Classify the caller's primary intent. billing: charges or refunds. " +
      "technical: product faults. cancel: closing the account. Pick the " +
      "single strongest intent; when none fits, other."
    );
  }
  return (
    "Return true only when the agent reads back the full shipping address " +
    "and the caller agrees. A partial address or an unanswered request is " +
    "false. Ignore billing addresses."
  );
}

const evaluatorOptions: Array<{
  label: string;
  value: AuthoringEvaluator;
}> = [
  { label: "Rule", value: "rule" },
  { label: "Judge", value: "judge" },
];

function errorStatus(error: unknown): MarkerAuthoringStatus {
  if (isApiRequestError(error)) {
    return {
      message: `${error.status.code}: ${error.status.message}`,
      stage: "api_error",
    };
  }
  return {
    message: error instanceof Error ? error.message : "Marker request failed.",
    stage: "api_error",
  };
}

function successMessage(mode: MarkerAuthoringMode): string {
  if (mode === "create") {
    return "Marker created.";
  }
  return mode === "revise" ? "Marker version saved." : "Marker automated.";
}

function submitLabel(mode: MarkerAuthoringMode, busy: boolean): string {
  if (busy) {
    return "Saving...";
  }
  if (mode === "create") {
    return "Create marker";
  }
  return mode === "revise" ? "Save version" : "Automate marker";
}

export function MarkerAuthoringForm({
  agentId,
  judgeModelPolicy,
  marker,
  mode,
  onCancel,
  onDirtyChange,
  onSubmit,
  renderHeader = (actions) => <PageHeader actions={actions} compact />,
}: MarkerAuthoringFormProps) {
  const formId = useId();
  const initial = useMemo(
    () => definitionSeed(marker?.current_revision),
    [marker],
  );
  const [rawDraft, setDraft] = useState<DefinitionDraft>(initial);
  const defaultModelRef =
    !judgeModelPolicy.allows_free_text && marker == null
      ? judgeModelPolicy.model_options.find((option) => option.is_default)
          ?.model_ref
      : undefined;
  const draft = {
    ...rawDraft,
    judgeModelRef: rawDraft.judgeModelRef || defaultModelRef || "",
  };
  const [status, setStatus] = useState<MarkerAuthoringStatus>({
    stage: "idle",
  });
  const judgeModelOptions = useMemo(
    () => judgeModelOptionsByRef(judgeModelPolicy),
    [judgeModelPolicy],
  );

  // Latest-callback ref so the reseed effect never re-runs (and wipes the
  // draft) just because a parent re-rendered with a new callback identity.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  });

  useEffect(() => {
    setDraft(initial);
    setStatus({ stage: "idle" });
    onDirtyChangeRef.current?.(false);
  }, [initial, mode]);

  const busy = status.stage === "submitting";
  const isLockedHumanRevision =
    mode === "revise" &&
    marker != null &&
    marker.current_revision.evaluator == null;

  function markDirty(): void {
    onDirtyChangeRef.current?.(true);
  }

  function updateDraft(patch: Partial<DefinitionDraft>): void {
    markDirty();
    setDraft((current) => {
      // A blank display name follows the description's first line, so a
      // fast author types once; an explicit name is never overwritten.
      if (
        patch.description !== undefined &&
        current.displayName.trim().length === 0
      ) {
        return {
          ...current,
          ...patch,
          displayName: patch.description.split("\n")[0] ?? "",
        };
      }
      if (patch.output !== undefined && patch.output !== current.output) {
        return {
          ...current,
          ...patch,
          assertionCases: [newCase(patch.output, 0)],
        };
      }
      return { ...current, ...patch };
    });
  }

  async function handleSubmit(
    event: SyntheticEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    try {
      const modelError = judgeModelError(draft, judgeModelPolicy);
      if (modelError != null) {
        setStatus({ message: modelError, stage: "validation_error" });
        return;
      }
      if (mode === "automate") {
        const markerId = marker?.id;
        const body = buildAutomateBody(draft);
        if (typeof body === "string") {
          setStatus({ message: body, stage: "validation_error" });
          return;
        }
        if (markerId == null) {
          setStatus({
            message: "Marker id is required.",
            stage: "validation_error",
          });
          return;
        }
        setStatus({ stage: "submitting" });
        onDirtyChangeRef.current?.(false);
        await onSubmit({ body, markerId, mode });
      } else {
        const body = buildRevisionBody(draft);
        if (typeof body === "string") {
          setStatus({ message: body, stage: "validation_error" });
          return;
        }
        if (mode === "create") {
          if (body.evaluator == null || body.spec == null) {
            setStatus({
              message: "Evaluation method is required.",
              stage: "validation_error",
            });
            return;
          }
          setStatus({ stage: "submitting" });
          onDirtyChangeRef.current?.(false);
          await onSubmit({
            body: { ...body, evaluator: body.evaluator, spec: body.spec },
            mode,
          });
        } else {
          const markerId = marker?.id;
          if (markerId == null) {
            setStatus({
              message: "Marker id is required.",
              stage: "validation_error",
            });
            return;
          }
          setStatus({ stage: "submitting" });
          onDirtyChangeRef.current?.(false);
          await onSubmit({ body, markerId, mode });
        }
      }
      setStatus({ message: successMessage(mode), stage: "success" });
    } catch (error) {
      onDirtyChangeRef.current?.(true);
      setStatus(errorStatus(error));
    }
  }

  function updateLocator(patch: Partial<LocatorDraft>): void {
    markDirty();
    setDraft((current) => ({
      ...current,
      locator: { ...current.locator, ...patch },
    }));
  }

  function updateCase(id: number, patch: Partial<AssertionCaseDraft>): void {
    markDirty();
    setDraft((current) => ({
      ...current,
      assertionCases: current.assertionCases.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    }));
  }

  function addCase(): void {
    markDirty();
    setDraft((current) => {
      const id =
        Math.max(-1, ...current.assertionCases.map((item) => item.id)) + 1;
      return {
        ...current,
        assertionCases: [
          ...current.assertionCases,
          newCase(current.output, id),
        ],
      };
    });
  }

  function removeCase(id: number): void {
    markDirty();
    setDraft((current) => ({
      ...current,
      assertionCases: current.assertionCases.filter((item) => item.id !== id),
    }));
  }

  const showOutputSpec = draft.output !== "" && outputSpecApplies(draft);
  const isAuthoringContent = mode !== "automate";
  const hasOutput = draft.output !== "";
  const hasEvaluator = draft.evaluator !== "" || isLockedHumanRevision;
  // Hosted always uses the picker (an empty allowlist renders it disabled);
  // self-managed uses it whenever a catalog is declared.
  const usesModelPicker =
    !judgeModelPolicy.allows_free_text ||
    judgeModelPolicy.model_refs.length > 0;
  const isVerdict = draft.assertionMode === "verdict";
  const showDetailsRow =
    isAuthoringContent &&
    (hasScopeParams(draft.locator) ||
      draft.reviewPolicy === "sample" ||
      isVerdict);

  const resultHandlingHelp = (
    <HelpTip label="About result handling">
      {"Record value only stores the value for reports. Apply pass, warn, " +
        "or fail adds conditions that turn the value into a verdict; the " +
        "first matching condition wins. The value is stored either way, so " +
        "conditions can change later without re-running the judge."}
    </HelpTip>
  );

  // Single-viewport Authoring (UI-LAW §4c): a centered column in a
  // fixed-height page, actions in the route header. Only the name and
  // output shape show first; choosing a shape reveals the definition and
  // from there every row is full — a dependent control (scope params,
  // sample rate, default verdict) joins one shared details row instead of
  // leaving an empty cell beside its parent.
  const actions = (
    <>
      <StatusMessage status={status} />
      {onCancel ? (
        <Button
          disabled={busy}
          type="button"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
      ) : null}
      <Button disabled={busy} form={formId} type="submit">
        {submitLabel(mode, busy)}
      </Button>
    </>
  );

  const form = (
    <form
      id={formId}
      className="marker-authoring"
      onSubmit={(event) => void handleSubmit(event)}
    >
      <Stack className="marker-authoring__body" gap={4}>
        <Grid columns={2} gap={4}>
          {isAuthoringContent ? (
            <>
              <TextInput
                disabled={busy}
                label="Display name"
                placeholder="Address confirmed"
                required
                value={draft.displayName}
                onChange={(event) => {
                  updateDraft({ displayName: event.currentTarget.value });
                }}
              />
              <Select
                disabled={busy}
                help={
                  <HelpTip
                    docHref={docUrl("guides/define-a-marker")}
                    docLabel="Define a marker"
                    label="About output types"
                  >
                    {"**Boolean** answers yes or no. **Number** returns a " +
                      "score inside the range you set. **Category** returns " +
                      "one of the categories you list. Choose the shape " +
                      "first: every other control is typed by it."}
                  </HelpTip>
                }
                label="Output type"
                required
                value={draft.output}
                onChange={(event) => {
                  updateDraft({
                    output: event.currentTarget.value as MarkerOutput | "",
                  });
                }}
              >
                <option value="">Select output type</option>
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </>
          ) : null}

          {hasOutput ? (
            <>
              {isAuthoringContent && showOutputSpec && draft.output !== "" ? (
                <OutputSpecFields
                  busy={busy}
                  draft={draft}
                  output={draft.output}
                  onChange={updateDraft}
                />
              ) : null}

              {isAuthoringContent ? (
                <GridItem span="full">
                  <Textarea
                    disabled={busy}
                    help={
                      <HelpTip
                        docHref={docUrl("concepts/markers")}
                        docLabel="Markers"
                        label="About the description"
                      >
                        {"Shown beside every Mark in results, reports, and " +
                          "review, so a reader knows what the value means. " +
                          "It is not sent to the judge: grading instructions " +
                          "belong in the Rubric."}
                      </HelpTip>
                    }
                    label="Description"
                    placeholder={
                      descriptionPlaceholder[draft.output as MarkerOutput]
                    }
                    rows={3}
                    value={draft.description}
                    onChange={(event) => {
                      updateDraft({ description: event.currentTarget.value });
                    }}
                  />
                </GridItem>
              ) : null}

              <Select
                disabled={busy || isLockedHumanRevision}
                help={
                  <HelpTip label="About evaluation methods">
                    {"**Rule** matches text deterministically and never " +
                      "calls a model. **Judge** sends the transcript and " +
                      "your Rubric to an LLM and records its typed answer."}
                  </HelpTip>
                }
                label="Evaluation method"
                required={!isLockedHumanRevision}
                value={draft.evaluator}
                onChange={(event) => {
                  updateDraft({
                    evaluator: event.currentTarget.value as
                      | AuthoringEvaluator
                      | "",
                  });
                }}
              >
                <option value="">
                  {isLockedHumanRevision ? "Human-only" : "Select method"}
                </option>
                {evaluatorOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {draft.evaluator === "rule" ? (
                <Select
                  disabled={busy}
                  label="Match type"
                  value={draft.ruleKind}
                  onChange={(event) => {
                    updateDraft({
                      ruleKind: event.currentTarget.value as "regex" | "string",
                    });
                  }}
                >
                  <option value="string">Contains text</option>
                  <option value="regex">Regular expression</option>
                </Select>
              ) : draft.evaluator === "judge" && usesModelPicker ? (
                <ModelSelect
                  disabled={busy}
                  label="Judge model"
                  value={draft.judgeModelRef}
                  onChange={(modelRef) => {
                    updateDraft({ judgeModelRef: modelRef });
                  }}
                  options={judgeModelPolicy.model_refs.map((modelRef) => {
                    const option = judgeModelOptions.get(modelRef);
                    const tier = option?.tier;
                    return {
                      value: modelRef,
                      label: judgeModelOptionLabel(modelRef, option),
                      providerId: option?.provider ?? undefined,
                      group: tier
                        ? tier.charAt(0).toUpperCase() + tier.slice(1)
                        : "Models",
                      estimatedCredits:
                        option?.estimated_credits_per_evaluation,
                    };
                  })}
                />
              ) : draft.evaluator === "judge" ? (
                <TextInput
                  disabled={busy}
                  help={
                    <HelpTip label="About the model reference">
                      {"Any model id the configured judge endpoint serves. " +
                        "Set MARKER_JUDGE_MODEL_CATALOG to offer a " +
                        "dropdown here."}
                    </HelpTip>
                  }
                  label="Model reference"
                  placeholder="gpt-5.6-luna"
                  required
                  value={draft.judgeModelRef}
                  onChange={(event) => {
                    updateDraft({ judgeModelRef: event.currentTarget.value });
                  }}
                />
              ) : null}

              {draft.evaluator === "judge" ? (
                <GridItem span="full">
                  <VariablePatternField
                    agentId={agentId ?? ""}
                    disabled={busy}
                    help={
                      <HelpTip
                        docHref={docUrl("guides/llm-judge-markers")}
                        docLabel="LLM judge markers"
                        label="About the rubric"
                      >
                        {"The judge reads the transcript, this Rubric, and " +
                          "the required value shape. Write it like a " +
                          "grader's checklist: what earns each value, what " +
                          "does not, and what to ignore. Name the exact " +
                          "value to return for each case."}
                      </HelpTip>
                    }
                    label="Rubric"
                    multiline
                    placeholder={rubricPlaceholder(draft)}
                    value={draft.judgeRubric}
                    onChange={(judgeRubric) => {
                      updateDraft({ judgeRubric });
                    }}
                  />
                </GridItem>
              ) : draft.evaluator === "rule" ? (
                <GridItem span="full">
                  {draft.ruleKind === "regex" ? (
                    <TextInput
                      disabled={busy}
                      help={
                        <HelpTip label="About rule patterns">
                          {"Matched against the scoped transcript text using " +
                            "Python regular expression syntax. Variables are not supported. The match count is " +
                            "the number, the matched text is the category."}
                        </HelpTip>
                      }
                      label="Regular expression"
                      placeholder="\\b(refund|chargeback)\\b"
                      required
                      value={draft.ruleValue}
                      onChange={(event) => {
                        updateDraft({ ruleValue: event.currentTarget.value });
                      }}
                    />
                  ) : (
                    <VariablePatternField
                      agentId={agentId ?? ""}
                      disabled={busy}
                      help={
                        <HelpTip label="About rule patterns">
                          {
                            "Find text in the scoped Transcript. The match count is the number; the matched text is the category. Insert variables to match each simulated call's values."
                          }
                        </HelpTip>
                      }
                      label="Text to find"
                      placeholder="your order has shipped"
                      value={draft.ruleValue}
                      onChange={(ruleValue) => {
                        updateDraft({ ruleValue });
                      }}
                    />
                  )}
                </GridItem>
              ) : null}

              {/* One settings row: three selects, no filler cells. */}
              <GridItem span="full">
                <Grid columns={3} gap={4}>
                  {isAuthoringContent ? (
                    <ScopeSelect
                      agentId={agentId}
                      busy={busy}
                      draft={draft.locator}
                      onChange={updateLocator}
                    />
                  ) : null}
                  {isAuthoringContent ? (
                    <ReviewPolicySelect
                      busy={busy}
                      policy={draft.reviewPolicy}
                      onPolicyChange={(reviewPolicy) => {
                        updateDraft({ reviewPolicy });
                      }}
                    />
                  ) : null}
                  {hasEvaluator ? (
                    <ResultHandlingSelect
                      busy={busy}
                      help={resultHandlingHelp}
                      mode={draft.assertionMode}
                      onModeChange={(assertionMode) => {
                        updateDraft({ assertionMode });
                      }}
                    />
                  ) : null}
                </Grid>
              </GridItem>

              {/* Details row: only the dependents the settings above ask
                  for, so the page never reserves empty space. */}
              {showDetailsRow ? (
                <GridItem span="full">
                  <Grid columns={3} gap={4}>
                    <ScopeParams
                      agentId={agentId}
                      busy={busy}
                      draft={draft.locator}
                      onChange={updateLocator}
                    />
                    {draft.reviewPolicy === "sample" ? (
                      <ReviewSampleField
                        busy={busy}
                        sampleRate={draft.reviewSampleRate}
                        onSampleRateChange={(reviewSampleRate) => {
                          updateDraft({ reviewSampleRate });
                        }}
                      />
                    ) : null}
                    {isVerdict ? (
                      <AssertionDefaultSelect
                        busy={busy}
                        defaultVerdict={draft.defaultVerdict}
                        onDefaultChange={(defaultVerdict) => {
                          updateDraft({ defaultVerdict });
                        }}
                      />
                    ) : null}
                  </Grid>
                </GridItem>
              ) : null}

              {hasEvaluator && isVerdict ? (
                <GridItem span="full">
                  <AssertionCaseList
                    busy={busy}
                    cases={draft.assertionCases}
                    categories={declaredCategories(draft)}
                    output={draft.output as MarkerOutput}
                    onAdd={addCase}
                    onCaseChange={updateCase}
                    onRemove={removeCase}
                  />
                </GridItem>
              ) : null}
            </>
          ) : null}
        </Grid>
      </Stack>
    </form>
  );

  return (
    <>
      {renderHeader(actions)}
      {form}
    </>
  );
}

function StatusMessage({ status }: { status: MarkerAuthoringStatus }) {
  if (status.stage === "idle") {
    return null;
  }
  if (status.stage === "api_error" || status.stage === "validation_error") {
    return <InlineError>{status.message}</InlineError>;
  }
  if (status.stage === "submitting") {
    return (
      <Inline gap={2}>
        <Spinner label="Saving marker" />
        <Text aria-live="polite" variant="muted">
          Saving marker.
        </Text>
      </Inline>
    );
  }
  return (
    <Text aria-live="polite" role="status">
      {status.message}
    </Text>
  );
}
